import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSearchParams } from 'hooks/useSearchParams';
import {
  BordBadge,
  BordButton,
  BordInfoBanner,
  BordInfoCard,
  BordTabBar,
  BordTabsGroupWithBadge,
  BordTooltip,
  TTabValue
} from 'bord-design-system';
import ChargerModule from 'components/chargerModule';
import { QuoteFilters, TQuoteFiltersComp } from './components/QuoteFilters/QuoteFilters';
import { EQuoteStatus, EQuoteType, IQuote, IQuoteFilters } from 'types/quote.types';
import { useQuotes } from '../hooks/useQuotes';
import { getTabs } from './utils';
import { EmptyStateByQuoteStatus } from './components/EmptyStateByQuoteStatus/EmptyStateByQuoteStatus';
import useUsersState from 'state/useUsersState';
import useDebounce from 'hooks/useDebounce';
import { TInputOnSort } from 'components/SortButton/SortButton';
import { getBordInfoCardItems } from './utils/getBordInfoCardItems';
import { useInfinitePagination } from '../hooks/useInfinitePagination';
import { QuoteRowSkeleton } from './components/QuoteRowSkeleton/QuoteRowSkeleton';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { PAGINATION_INITIAL_PAGE } from 'constants/constants';
import { QuoteRowListSkeleton } from './components/QuoteRowListSkeleton/QuoteRowListSkeleton';
import { QuotesSort } from './components/QuotesSort/QuotesSort';
import { useQuoteFiltersUrl } from './hooks/useQuoteFiltersUrl';
import { mapQuoteFiltersCompToApiFilters, parseQuoteFiltersFromSearchParams } from './utils/quoteFiltersUrl.utils';

import './QuoteScreen.scss';

export const QuoteScreen = () => {
  const history = useHistory();
  const [searchParams] = useSearchParams();
  const { users } = useUsersState();

  const [selectedStatus, setSelectedStatus] = useState(() => parseQuoteFiltersFromSearchParams(searchParams).status);
  const [filters, setFilters] = useState<IQuoteFilters>(() => parseQuoteFiltersFromSearchParams(searchParams).filters);
  const [searchText, setSearchText] = useState(() => parseQuoteFiltersFromSearchParams(searchParams).searchText);
  const [quoteFiltersComp, setQuoteFiltersComp] = useState(
    () => parseQuoteFiltersFromSearchParams(searchParams).quoteFiltersComp
  );

  const HEADER_INFO = { title: 'Cotizaciones', subtitle: '' };
  const INFO_SIDEBAR = { position: 'quote' };

  const {
    quotes,
    additionalCalcs,
    countByStatus,
    filterOptions,
    isLoading,
    getAllByStatus,
    getFilters,
    getCountsByStatus,
    clearQuotes
  } = useQuotes();

  const { hasMore, nextPage, page, reset: resetPagination, setHasMore } = useInfinitePagination();

  const { ref } = useInfiniteScroll({ hasMore, loading: isLoading, onLoadMore: nextPage });

  const textDebounced = useDebounce(searchText, 500);

  /**
   * Rehidrata filtros, pestaña y búsqueda desde la URL (navegación del historial).
   */
  const handleUrlFiltersChange = useCallback(
    (parsed: ReturnType<typeof parseQuoteFiltersFromSearchParams>) => {
      resetPagination();
      setSelectedStatus(parsed.status);
      setFilters(parsed.filters);
      setSearchText(parsed.searchText);
      setQuoteFiltersComp(parsed.quoteFiltersComp);
    },
    [resetPagination]
  );

  useQuoteFiltersUrl({
    status: selectedStatus,
    filters,
    searchText: textDebounced,
    onUrlChange: handleUrlFiltersChange
  });

  const tabs = useMemo(() => getTabs(countByStatus), [countByStatus]);

  const showInfoBannerToQuote = selectedStatus === EQuoteStatus.TO_QUOTE;

  const showInfoBanner = showInfoBannerToQuote;

  const showSkeleton = isLoading && page === PAGINATION_INITIAL_PAGE;

  const getInfoBannerText = () => {
    if (additionalCalcs?.withSLACTO) return 'SLA de CTO';
    if (selectedStatus === EQuoteStatus.TO_QUOTE) return 'SLA de la cotización';
    return 'Tiempo de aprobación';
  };

  const getItems = useMemo(
    () => (quote: IQuote) =>
      getBordInfoCardItems(quote, selectedStatus, users, () => {
        getFilters();
      }),
    [selectedStatus, users]
  );

  const search = (text: string) => setSearchText(text.trim());

  const handleChangeFilters = (filtersComp: TQuoteFiltersComp) => {
    resetPagination();
    setQuoteFiltersComp(filtersComp);
    setFilters(prev => ({
      ...prev,
      ...mapQuoteFiltersCompToApiFilters(filtersComp)
    }));
  };

  const handleSort = (values: TInputOnSort) => {
    resetPagination();
    setFilters(prev => ({
      ...prev,
      sortData: values?.sortData,
      sortValues: values?.sortValues
    }));
  };

  const handleChangeTab = (value: TTabValue) => {
    clearQuotes();
    resetPagination();
    setSelectedStatus(value as EQuoteStatus);
  };

  const openQuoteDetails = (quoteId: number) => {
    if (!quoteId) return;
    history.push(`/quote/details/${quoteId}`);
  };

  const getInfoCardVariant = (quote: IQuote) => {
    if (quote?.isExpired && selectedStatus === EQuoteStatus.TO_QUOTE) return 'danger';
    if (selectedStatus === EQuoteStatus.REJECTED) return 'disabled';
    return 'success';
  };

  const customClassName = selectedStatus === EQuoteStatus.PURCHASED ? 'purchased' : '';

  useEffect(() => {
    getAllByStatus(selectedStatus, { ...filters, page })
      .then(res => setHasMore(res?.hasMore))
      .catch(() => undefined);
  }, [selectedStatus, filters, page]);

  useEffect(() => {
    getCountsByStatus(filters);
  }, [filters]);

  useEffect(() => {
    getFilters();
  }, []);

  useEffect(() => {
    setFilters(prev => {
      if (prev.search === textDebounced) return prev;
      return { ...prev, search: textDebounced };
    });
    resetPagination();
  }, [textDebounced]);

  return (
    <div>
      <ChargerModule
        headerInfo={HEADER_INFO}
        infoSideBar={INFO_SIDEBAR}
        viewSearchBar
        searchText={searchText}
        setSearchText={search}
        searchbarPlaceholder="Buscar por Id de cotización"
        custonMarginRightButtonsContainer="380px"
      >
        <div className="quoteScreenLayout ">
          <div className="quoteScreenHeading ">
            <div className="layout">
              <div className="filterSection">
                <QuoteFilters
                  filterOptions={filterOptions!}
                  initialFiltersSelected={quoteFiltersComp}
                  onChange={handleChangeFilters}
                />
                <QuotesSort
                  initialSortBy={filters?.sortData?.[0]}
                  initialSortOrder={filters?.sortValues?.[0]}
                  onSort={handleSort}
                />
              </div>
              <BordTabBar
                defaultValue="quotes"
                tabs={[
                  { id: 'quotes', label: 'Cotizaciones' },
                  { id: 'products', label: 'Productos' }
                ]}
              />
            </div>
            <div className="layout">
              <BordTabsGroupWithBadge
                defaultValue={EQuoteStatus.TO_QUOTE}
                value={selectedStatus}
                tabs={tabs}
                onChange={handleChangeTab}
              />
            </div>
          </div>
          <div className="quoteScreenMain">
            {showInfoBannerToQuote && (
              <div className="infoBannerSection ">
                {additionalCalcs.withSLACTO && selectedStatus === EQuoteStatus.TO_QUOTE && (
                  <BordInfoBanner
                    primaryText="SLA de la cotización"
                    alertLabel={''}
                    rangeText="24hrs"
                  />
                )}
                <BordInfoBanner
                  primaryText={getInfoBannerText()}
                  rangeText={additionalCalcs.withSLACTO ? '72hrs' : '24hrs'}
                  alertLabel={
                    additionalCalcs?.expiredPendingQuotesCount
                      ? `${additionalCalcs?.expiredPendingQuotesCount} cotizaciones atrasadas`
                      : ''
                  }
                />
              </div>
            )}
            <div className={`quoteScreenList ${!showInfoBanner ? 'py-16' : ''}`}>
              <div className="layout">
                {showSkeleton ? (
                  <QuoteRowListSkeleton />
                ) : (
                  quotes?.map((quote, index) => (
                    <BordInfoCard
                      key={index}
                      customClassName={`customBordInfoCard ${customClassName}`}
                      variant={getInfoCardVariant(quote)}
                      header={{
                        icon: (
                          <div className="flag">
                            <img alt="country url" src={quote?.exchange?.flag} />
                          </div>
                        ),
                        title: quote?.exchange?.country,
                        description: (
                          <div className="quoteDescriptionCellLayout ">
                            {`Q°${quote.id}`}{' '}
                            {Boolean(quote.parentQuoteId) && (
                              <BordTooltip
                                message={
                                  <span>
                                    Cotización recotizada de{' '}
                                    <span className="quoteDuplicatedCellText">Q°{quote?.parentQuoteId}</span>
                                  </span>
                                }
                                position="bottomLeft"
                              >
                                <BordBadge label="Recotizada" variant="gradient" size="small" />
                              </BordTooltip>
                            )}
                            {quote?.quoteTypeCode === EQuoteType.EXPLORATORY && (
                              <BordBadge label="Exploratoria" variant="gradient" size="small" />
                            )}
                          </div>
                        )
                      }}
                      secondaryButtonProps={{
                        label: 'Consultar',
                        customClassName: 'bordInfoCardButton',
                        bordButtonStyle: 'outlined',
                        standardPaddingSize: 'squared',
                        onClick: () => openQuoteDetails(quote.id)
                      }}
                      items={getItems(quote)}
                    />
                  ))
                )}
                {hasMore && <QuoteRowSkeleton ref={ref} />}
              </div>
              {!quotes?.length && !textDebounced?.length && !isLoading && (
                <EmptyStateByQuoteStatus quoteStatus={selectedStatus} />
              )}
              {!quotes?.length && Boolean(textDebounced?.length) && !isLoading && (
                <EmptyStateByQuoteStatus quoteStatus={selectedStatus} noResults />
              )}
            </div>
          </div>
        </div>
      </ChargerModule>
    </div>
  );
};
